import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box,
    Sheet,
    Typography,
    Button,
    Checkbox,
    List,
    ListItem,
    ListItemDecorator,
    Divider,
    Alert,
    CircularProgress,
    Stack
} from '@mui/joy';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import WarningIcon from '@mui/icons-material/Warning';
import domain from '../utils/domain';
import { Student, Exam, Registration } from '../types/exam';

const Instructions: React.FC = () => {
    const navigate = useNavigate();
    const { examId, registrationHash } = useParams<{ examId: string; registrationHash: string }>();
    const [accepted, setAccepted] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string>('');
    const [data, setData] = useState<{
        registration: Registration;
        student: Student;
        exam: Exam;
    } | null>(null);

    // Define fetchExamDetails using useCallback
    const fetchExamDetails = useCallback(async () => {
        try {
            const response = await fetch(`${domain}/api/registrations/${examId}/${registrationHash}`);
            if (!response.ok) {
                throw new Error('Failed to fetch exam details');
            }
            const result = await response.json();
            setData(result);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load exam details');
        } finally {
            setIsLoading(false);
        }
    }, [examId, registrationHash]);

    useEffect(() => {
        // Load data from localstorage
        const studentData = localStorage.getItem('studentData');
        if (studentData) {
            setData(JSON.parse(studentData));
            setIsLoading(false);
        } else {
            fetchExamDetails();
        }
    }, [fetchExamDetails]);

    // Add scroll to top effect
    useEffect(() => {
        if (!isLoading && data) {
            window.scrollTo({
                top: 0
            });
        }
    }, [isLoading, data]);

    const handleProceed = async () => {
        if (accepted) {
            setIsLoading(true);
            try {
                const start_time = new Date().toISOString();
                console.log("start_time ISO String:", start_time);
                const response = await fetch(`${domain}/api/registrations/${examId}/${registrationHash}/start`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ start_time: start_time })
                });
                if (response.status === 400) {
                    throw new Error('Exam already completed or cancelled');
                }
                if (!response.ok) {
                    throw new Error('Failed to fetch exam details');
                }

                const result = await response.json();
                console.log("result:", result);

                // Set violation count
                const violation_count = result.violation_count || 0;
                localStorage.setItem('violationCount', violation_count.toString());

                // Calculate time remaining
                let timeRemaining;
                const maxDuration = (data?.exam?.duration || 0) * 60 * 1000; // Convert minutes to milliseconds
                if (result.message === 'Exam already started') {
                    // For resumed exam, use the time_spent_ms from server
                    timeRemaining = (data?.exam?.duration || 0) * 60 * 1000 - result.time_spent_ms;
                } else {
                    // For new exam, full duration
                    timeRemaining = (data?.exam?.duration || 0) * 60 * 1000;
                }

                // Convert to seconds for storage
                const timeRemainingSeconds = Math.floor(timeRemaining / 1000);
                console.log("timeRemaining (seconds):", timeRemainingSeconds);

                localStorage.removeItem('examTimeRemaining');
                localStorage.setItem('examTimeRemaining', timeRemainingSeconds.toString());

                // Mark that we've started the exam session
                sessionStorage.setItem('examSessionStarted', 'true');
                navigate(`/exam/writeexam/${examId}/${registrationHash}`);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to navigate to exam, Please check your internet connection and try again or contact support. email: support@vectorindia.org, call: +91 98666 66699');
            } finally {
                setIsLoading(false);
            }
        }
    };

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <CircularProgress size="lg" />
            </Box>
        );
    }

    if (error || !data || !data.student) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <Alert color="danger" variant="soft" size="lg">
                    {error || 'Failed to load exam details'}
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{
            minHeight: '100vh',
            bgcolor: 'white',
            py: 4,
            px: 2
        }}>
            <Sheet
                variant="outlined"
                sx={{
                    maxWidth: '900px',
                    margin: '0 auto',
                    borderRadius: 'lg',
                    overflow: 'hidden',
                    borderColor: '#ea7a23'
                }}
            >
                {/* Header with Logo */}
                <Box sx={{
                    bgcolor: 'white',
                    p: 3,
                    height: '200px',
                    borderBottom: '1px solid',
                    borderColor: '#ea7a23',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                }}>
                    <Box
                        sx={{
                            width: '300px',
                            height: '150px',
                            margin: '0 auto',
                            mb: 2,
                            position: 'relative'
                        }}
                    >
                        <img
                            src={`${process.env.PUBLIC_URL}/logo-vector.svg`}
                            alt="Vector India Logo"
                            style={{
                                width: '100%',
                                height: '100px',
                                objectFit: 'contain'
                            }}
                        />
                    </Box>
                    <Typography
                        level="h2"
                        sx={{
                            textAlign: 'center',
                            color: '#ea7a23',
                            fontSize: '1.75rem'
                        }}
                    >
                        Exam Instructions
                    </Typography>
                </Box>

                <Box sx={{ p: 4 }}>
                    <Alert
                        variant="soft"
                        color="danger"
                        startDecorator={<WarningIcon sx={{ fontSize: 30, color: "danger" }} />}
                        sx={{ mb: 4 }}
                    >
                        Please read and accept these instructions carefully before proceeding with the exam
                    </Alert>

                    <Stack spacing={4}>
                        {/* General Instructions */}
                        <Box>
                            <Typography level="title-lg" sx={{ mb: 2, color: '#ea7a23' }}>
                                General Instructions
                            </Typography>
                            <List>
                                <ListItem>
                                    <ListItemDecorator>•</ListItemDecorator>
                                    This is a {data.exam.duration} minutes exam containing {data.exam.total_marks} Multiple Choice Questions
                                </ListItem>
                                <ListItem>
                                    <ListItemDecorator>•</ListItemDecorator>
                                    Negative Marking Applicable for WRONG Answers. Each WRONG Answer carries -{data.exam.negative_marking} Marks
                                </ListItem>
                                <ListItem>
                                    <ListItemDecorator>•</ListItemDecorator>
                                    This exam is browser and video proctored, which means, we capture if you try to exit full screen mode, copy-paste, tab change, or any other suspicious browser activities
                                </ListItem>
                                <ListItem>
                                    <ListItemDecorator>•</ListItemDecorator>
                                    Your proctored image will be verified at the time of admission
                                </ListItem>
                                <ListItem>
                                    <ListItemDecorator>•</ListItemDecorator>
                                    Discounts will be applicable based on verification of your Examination Violation Counts
                                </ListItem>
                            </List>
                        </Box>

                        <Divider />

                        {/* Proctoring Guidelines */}
                        <Box>
                            <Typography level="title-lg" sx={{ mb: 2, color: '#ea7a23' }}>
                                Proctoring Guidelines
                            </Typography>
                            <List>
                                <ListItem>
                                    <ListItemDecorator>•</ListItemDecorator>
                                    DON'T use: 1) Mobile phone 2) Headphone/Earphones 3) Multiple Screen. Using them is strictly prohibited
                                </ListItem>
                                <ListItem>
                                    <ListItemDecorator>•</ListItemDecorator>
                                    You CAN USE paper-pen during this exam for your rough-work
                                </ListItem>
                                <ListItem>
                                    <ListItemDecorator>•</ListItemDecorator>
                                    During Video proctoring make sure you are always facing the camera with sufficient light on your face
                                </ListItem>
                                <ListItem>
                                    <ListItemDecorator>•</ListItemDecorator>
                                    The exam will be auto-terminated if you open new tabs more than 3 times
                                </ListItem>
                                <ListItem>
                                    <ListItemDecorator>•</ListItemDecorator>
                                    DON'T take pictures or snapshot of the exam papers during the test
                                </ListItem>
                            </List>
                        </Box>

                        <Divider />

                        {/* Browser/Internet Guidelines */}
                        <Box>
                            <Typography level="title-lg" sx={{ mb: 2, color: '#ea7a23' }}>
                                Browser/Internet Connection Guidelines
                            </Typography>
                            <List>
                                <ListItem>
                                    <ListItemDecorator>•</ListItemDecorator>
                                    Please close all other applications and tabs before taking the test
                                </ListItem>
                                <ListItem>
                                    <ListItemDecorator>•</ListItemDecorator>
                                    For a better experience, we recommend the latest version of Google Chrome/Chromium, Mozilla Firefox browsers
                                </ListItem>
                                <ListItem>
                                    <ListItemDecorator>•</ListItemDecorator>
                                    Take the test from a computer with a stable internet connection
                                </ListItem>
                                <ListItem>
                                    <ListItemDecorator>•</ListItemDecorator>
                                    If you get disconnected, We will restore the session. Once the session started you have to complete the exam within {data.exam.duration} minutes. if not completed, the exam will be auto-submitted.
                                </ListItem>
                            </List>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 4 }}>
                            <Checkbox
                                checked={accepted}
                                onChange={(e) => setAccepted(e.target.checked)}
                                label="I have read and agree to the exam terms and conditions"
                                color="danger"
                                sx={{
                                    '--joy-palette-danger-500': '#ea7a23',
                                    '--joy-palette-danger-600': '#ea7a23',
                                    '--joy-palette-danger-plainActiveBg': '#ea7a23',
                                    '--joy-palette-danger-plainHoverBg': 'rgba(234, 122, 35, 0.08)',
                                    '&:hover': {
                                        backgroundColor: 'rgba(234, 122, 35, 0.08)'
                                    }
                                }}
                            />
                            <Button
                                disabled={!accepted}
                                onClick={handleProceed}
                                endDecorator={<ArrowForwardIcon />}
                                sx={{
                                    bgcolor: '#ea7a23',
                                    '&:hover': { bgcolor: '#d86a1f' },
                                    '&.Mui-disabled': {
                                        border: '2px solid #ea7a23',
                                        opacity: 1.0
                                    }
                                }}
                            >
                                Proceed to Exam
                            </Button>
                        </Box>
                    </Stack>
                </Box>
            </Sheet>
        </Box>
    );
};

export default Instructions; 